import { useEffect, useRef } from 'react';
import { useEodBidGate, useFeedbackCycleId } from '~/domain/BestInDental';
import { BidFeedbackDialog } from '../BidFeedbackDialog';

interface IBidEodFeedbackStepProps {
  jobId: string | undefined;
  /** The counterparty being reviewed (the professional who worked the shift). */
  applicantUserId: string | undefined;
  /** Display name for `{NAME}` substitution — the gate response omits it. */
  applicantName: string | null;
  isTriggered: boolean;
  onComplete: () => void;
}

/**
 * CS-5 EOD feedback step (GOTU-5643). Sits in the rating "Send" chain
 * between the click handler and `RatingFeedbackSuccessHandler` (see
 * `JobSummaryRating.tsx`), mirroring that handler's always-mounted
 * `isTriggered` prop pattern.
 *
 * The gate is fetched for `(feedbackCycle, applicant)`: the cycle is the
 * server-resolved one from `GET /feedback/pending` (shared cache, GOTU-6571),
 * the applicant is the shift's professional. Fails open — every state other than an explicit,
 * fully-populated open gate (`showStep: false`, including the
 * `CLOSED_EOD_GATE` placeholder shown while loading, on fetch error, or before
 * the cycle/applicant resolve) calls `onComplete()` immediately instead of
 * rendering, so this step can never block the revenue-critical EOD flow.
 */
export const BidEodFeedbackStep = ({
  jobId,
  applicantUserId,
  applicantName,
  isTriggered,
  onComplete,
}: IBidEodFeedbackStepProps) => {
  const cycleId = useFeedbackCycleId();
  const { data: gate } = useEodBidGate(cycleId, applicantUserId);
  const hasCompletedRef = useRef(false);

  const isGateOpen = Boolean(
    jobId && gate?.showStep && gate.cycleId && gate.applicantUserId && gate.questions?.length,
  );

  useEffect(() => {
    if (isTriggered && !isGateOpen && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      onComplete();
    }
  }, [isTriggered, isGateOpen, onComplete]);

  if (
    !isTriggered ||
    !isGateOpen ||
    !jobId ||
    !gate?.cycleId ||
    !gate.applicantUserId ||
    !gate.questions?.length
  ) {
    return null;
  }

  return (
    <BidFeedbackDialog
      jobId={jobId}
      cycleId={gate.cycleId}
      applicantUserId={gate.applicantUserId}
      applicantName={applicantName}
      questions={gate.questions}
      onComplete={onComplete}
    />
  );
};
